<template>
  <div>
    <vs-dialog v-model="show" blur prevent-close not-close>
      <template #header>
        <h4>Importazione Massiva Città</h4>
      </template>
      <div>
        <vs-row align="center" justify="space-between" style="margin-bottom: 16px;">
          <vs-button @click="downloadExample" icon size="large">
            <i class="bx bx-download"></i>
            Scarica XLSX di esempio
          </vs-button>
          <label class="vs-upload-label">
            <vs-button icon size="large" @click="$refs.fileInput.click()">
              <i class="bx bx-upload"></i>
              Carica XLSX
            </vs-button>
            <input type="file" accept=".xlsx" @change="onFileChange" style="display: none;" ref="fileInput" />
          </label>
        </vs-row>

        <div v-if="parseErrors.length" style="margin-top: 10px;">
          <vs-alert color="danger" icon="warning" v-for="err in parseErrors" :key="err" style="margin-bottom: 4px;">
            {{ err }}
          </vs-alert>
        </div>

        <div v-if="parsedRows.length" style="margin-top: 24px;">
          <div v-if="importing" style="margin-bottom: 12px;">
            <vs-progress :percent="progress" color="primary" style="width: 100%; margin-bottom: 8px;" />
            <div style="font-size: 14px;">{{ progressText }}</div>
          </div>
          <h5 style="margin-bottom: 12px;">Anteprima (prime 5 righe):</h5>
          <vs-table style="max-width: 100%; overflow-x: auto;">
            <template #thead>
              <vs-tr>
                <vs-th v-for="col in columns" :key="col" style="white-space: nowrap;">{{ col }}</vs-th>
              </vs-tr>
            </template>
            <template #tbody>
              <vs-tr v-for="row in parsedRows.slice(0,5)" :key="row">
                <vs-td v-for="col in columns" :key="col" style="white-space: nowrap;">{{ row[col] }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <vs-row justify="end" style="margin-top: 20px;">
            <vs-button :loading="importing" @click="importAll" size="large" color="primary" icon>
              <i class="bx bx-import"></i>
              Importa Tutto
            </vs-button>
          </vs-row>
          <div v-if="importResult" style="margin-top: 10px;">
            <vs-alert color="success" icon="check_circle">
              {{ importResult }}
            </vs-alert>
          </div>
          <div v-if="failedRows.length" style="margin-top: 10px;">
            <vs-button color="warning" icon @click="exportFailedRows">
              <i class="bx bx-download"></i>
              Esporta righe fallite (XLSX)
            </vs-button>
          </div>
        </div>
      </div>
      <template #footer>
        <vs-row justify="end">
          <vs-button @click="close" danger size="large">Chiudi</vs-button>
        </vs-row>
      </template>
    </vs-dialog>

    <!-- UUID Prompt Dialog -->
    <vs-dialog v-model="showUuidPrompt" blur not-close>
      <template #header>
        <h4>UUID mancanti o non validi</h4>
      </template>
      <div>
        <div v-if="missingId">
          Il file XLSX caricato non contiene la colonna <b>id</b>.<br>
          Vuoi che generiamo automaticamente un UUID per ogni riga e ti forniamo un nuovo XLSX da scaricare?
        </div>
        <div v-else>
          Le seguenti righe hanno un <b>id</b> non valido (deve essere un UUID):<br>
          <span style="color: #d32f2f;">{{ invalidUuidRows.join(', ') }}</span><br>
          Vuoi che generiamo automaticamente UUID validi per queste righe e ti forniamo un nuovo XLSX da scaricare?
        </div>
      </div>
      <template #footer>
        <vs-row justify="end">
          <vs-button @click="generateUuidsAndDownload" color="primary">Sì, genera UUID e scarica XLSX</vs-button>
          <vs-button @click="cancelUuidPrompt" danger>Chiudi</vs-button>
        </vs-row>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { apiCall } from '../../utils/ApiMiddleware';

export default {
  name: 'MassiveCityImport',
  props: {
    show: { type: Boolean, required: true },
    onClose: { type: Function, required: true }
  },
  data() {
    return {
      columns: ['id', 'country', 'state', 'district', 'city'],
      parsedRows: [],
      parseErrors: [],
      importing: false,
      importResult: null,
      showUuidPrompt: false,
      invalidUuidRows: [],
      missingId: false,
      progress: 0,
      progressText: '',
      failedRows: [],
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.init();
      }
    }
  },
  methods: {
    init() {
      this.columns = ['id', 'country', 'state', 'district', 'city'];
      this.parsedRows = [];
      this.parseErrors = [];
      this.importResult = null;
      this.failedRows = [];
      this.progress = 0;
      this.progressText = '';
    },
    close() {
      this.$emit('update:show', false);
      this.onClose();
    },
    downloadExample() {
      const exampleRow = {
        id: '123e4567-e89b-12d3-a456-426614174000',
        country: 'Italia',
        state: 'Lazio',
        district: 'Roma',
        city: 'Roma'
      };
      const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: this.columns });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Esempio');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'example_cities.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext === 'xlsx') {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
            this.handleParsedRows(json);
          } catch (err) {
            this.parseErrors = [err.message];
          }
        };
        reader.onerror = (err) => {
          this.parseErrors = [err.message];
        };
        reader.readAsArrayBuffer(file);
      } else {
        this.parseErrors = ['Formato file non supportato. Usa solo XLSX.'];
      }
    },
    handleParsedRows(rows) {
      this.parsedRows = rows;
      this.parseErrors = [];
      this.showUuidPrompt = false;
      this.invalidUuidRows = [];
      this.missingId = false;
      // Validate columns
      const missing = this.columns.filter(c => !(c in this.parsedRows[0]));
      if (missing.length) {
        this.parseErrors.push('Colonne mancanti: ' + missing.join(', '));
      }
      // Check for id column and UUID validity
      if (!this.parsedRows.length) return;
      if (!('id' in this.parsedRows[0])) {
        this.missingId = true;
        this.showUuidPrompt = true;
        return;
      }
      // UUID regex
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      this.invalidUuidRows = [];
      for (let i = 0; i < this.parsedRows.length; i++) {
        const id = this.parsedRows[i].id;
        if (!id || !uuidRegex.test(id)) {
          this.invalidUuidRows.push(i + 2);
        }
      }
      if (this.invalidUuidRows.length) {
        this.showUuidPrompt = true;
      }
    },
    generateUuidsAndDownload() {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      function uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      if (this.missingId) {
        this.parsedRows.forEach(row => { row.id = uuidv4(); });
      } else {
        for (let i = 0; i < this.parsedRows.length; i++) {
          if (!uuidRegex.test(this.parsedRows[i].id)) {
            this.parsedRows[i].id = uuidv4();
          }
        }
      }
      const cols = this.columns;
      const rows = this.parsedRows.map(row => {
        const r = {};
        for (const col of cols) r[col] = row[col] ?? '';
        return r;
      });
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: cols });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Città');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cities_con_uuid.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.showUuidPrompt = false;
      this.parseErrors = [];
      this.parsedRows = [];
      this.missingId = false;
      this.invalidUuidRows = [];
      this.$vs.notification({
        color: 'primary',
        title: 'Scarica e ricarica',
        text: 'UUID generati. Scarica il nuovo XLSX e ricaricalo per continuare.'
      });
    },
    cancelUuidPrompt() {
      this.showUuidPrompt = false;
      this.missingId = false;
      this.invalidUuidRows = [];
    },
    exportFailedRows() {
      if (!this.failedRows.length) return;
      const cols = [...this.columns, 'motivation'];
      const rows = this.failedRows.map(row => {
        const r = {};
        for (const col of this.columns) r[col] = row[col] ?? '';
        r.motivation = row.motivation || '';
        return r;
      });
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: cols });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Città_Fallite');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'citta_fallite.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    async importAll() {
      this.importing = true;
      this.importResult = null;
      this.progress = 0;
      this.progressText = '';
      this.failedRows = [];
      let success = 0, fail = 0;
      for (let i = 0; i < this.parsedRows.length; i++) {
        const row = this.parsedRows[i];
        let motivation = '';
        this.progress = Math.round(((i + 1) / this.parsedRows.length) * 100);
        this.progressText = `Elaborazione riga ${i + 1} di ${this.parsedRows.length}`;
        try {
          // 1. Country
          let countryId = null;
          const countryRes = await apiCall('GET', '/places/countries', { search: row.country });
          let country = (countryRes.status === 200 && Array.isArray(countryRes.data.data) && countryRes.data.data.length)
            ? countryRes.data.data.find(c => c.name.toLowerCase() === (row.country || '').toLowerCase())
            : null;
          if (!country) {
            // Create country
            const createCountry = await apiCall('POST', '/places/countries', { name: row.country });
            if (createCountry.status === 200 && createCountry.data.id) {
              countryId = createCountry.data.id;
            } else {
              motivation = 'Errore creazione country';
              throw new Error(motivation);
            }
          } else {
            countryId = country.id;
          }

          // 2. State
          let stateId = null;
          const stateRes = await apiCall('GET', '/places/states', { country_id: countryId, search: row.state });
          let state = (stateRes.status === 200 && Array.isArray(stateRes.data.data) && stateRes.data.data.length)
            ? stateRes.data.data.find(s => s.name.toLowerCase() === (row.state || '').toLowerCase())
            : null;
          if (!state) {
            const createState = await apiCall('POST', '/places/states', { name: row.state, country_id: countryId });
            if (createState.status === 200 && createState.data.id) {
              stateId = createState.data.id;
            } else {
              motivation = 'Errore creazione state';
              throw new Error(motivation);
            }
          } else {
            stateId = state.id;
          }

          // 3. District
          let districtId = null;
          const districtRes = await apiCall('GET', '/places/districts', { state_id: stateId, search: row.district });
          let district = (districtRes.status === 200 && Array.isArray(districtRes.data.data) && districtRes.data.data.length)
            ? districtRes.data.data.find(d => d.name.toLowerCase() === (row.district || '').toLowerCase())
            : null;
          if (!district) {
            const createDistrict = await apiCall('POST', '/places/districts', { name: row.district, state_id: stateId });
            if (createDistrict.status === 200 && createDistrict.data.id) {
              districtId = createDistrict.data.id;
            } else {
              motivation = 'Errore creazione district';
              throw new Error(motivation);
            }
          } else {
            districtId = district.id;
          }

          // 4. City
          const cityRes = await apiCall('GET', '/places/cities', { district_id: districtId, search: row.city });
          let city = (cityRes.status === 200 && Array.isArray(cityRes.data.data) && cityRes.data.data.length)
            ? cityRes.data.data.find(c => c.name.toLowerCase() === (row.city || '').toLowerCase())
            : null;
          if (!city) {
            const createCity = await apiCall('POST', '/places/cities', { id: row.id, name: row.city, district_id: districtId });
            if (createCity.status === 200 && createCity.data.id) {
              // Created successfully
              success++;
            } else {
              motivation = 'Errore creazione city';
              throw new Error(motivation);
            }
          } else {
            // City already exists, skip creation
            success++;
          }
        } catch (err) {
          fail++;
          this.failedRows.push({ ...row, motivation: motivation || (err && err.message) || 'Errore sconosciuto' });
        }
      }
      this.importing = false;
      this.progress = 100;
      this.progressText = 'Importazione completata';
      this.importResult = `Importazione completata: ${success} riuscite, ${fail} fallite.`;
    },
  }
};
</script>
